<template>
    <g-card class="conversions-chart">
        <g-typography tag="h4" font-weight="semibold" class="g-mb-5">Total Conversions</g-typography>

        <div class="chart-container g-mb-6">
            <g-loader v-if="isLoading" type="absolute" />
            <g-area-chart
                v-else
                :colors="['#228EC6']"
                :dataset="dataset"
                :x-axis-categories="categories"
                :override-chart-config="chartOverrides"
                data-testid="conversions_chart"
            />
        </div>

        <g-row type="flex" justify="center" :gutter="16">
            <g-column v-for="(range, i) in TOTAL_CONVERSIONS_RANGES" :key="i">
                <g-button
                    :disabled="isLoading"
                    shape="round"
                    theme="live"
                    :class="{ selected: selectedRange === range }"
                    :data-testid="`filter_${TOTAL_CONVERSIONS_RANGE_LABELS[range]}`"
                    @click="updateSeries(range)"
                >
                    {{ TOTAL_CONVERSIONS_RANGE_LABELS[range] }}
                </g-button>
            </g-column>
        </g-row>
    </g-card>
</template>

<script>
    import moment from 'moment-timezone';
    import { ref, onBeforeMount } from 'vue-demi';
    import { useContactStore } from '../../../../stores';
    import useInsights from '../../../../src/composables/use-insights';
    import useEventTracking from '../../../../src/composables/use-event-tracking';
    import { EVENT_NAME_CONVERSION_CHART_ADJUSTED } from '../../../../app-v2/constants/analytics-events';
    import {
        TOTAL_CONVERSIONS_RANGES,
        TOTAL_CONVERSIONS_RANGE_LABELS,
        TOTAL_CONVERSIONS_RANGE_ALL,
        TOTAL_CONVERSIONS_RANGE_WEEK,
        TOTAL_CONVERSIONS_RANGE_MONTH
    } from '../../../constants/insights';

    export default {
        name: 'ConversionsChart',

        setup() {
            const contact = useContactStore().data;
            const events = useEventTracking();
            const isLoading = ref(false);
            const dataset = ref(null);
            const unit = ref(null);
            const categories = ref(null);
            const selectedRange = ref(TOTAL_CONVERSIONS_RANGE_ALL);
            const dateRangeFrom = ref(null);
            const dateRangeTo = ref(null);

            const chartOverrides = {
                chart: {
                    height: 250
                },
                legend: {
                    layout: 'vertical',
                    align: 'right',
                    verticalAlign: 'top',
                    floating: true
                },
                tooltip: {
                    formatter() {
                        return this.points.reduce((date, point) => {
                            return (
                                date +
                                '<br/>' +
                                `<span style="color:${point.series.color};line-height: 20px">●</span> ` +
                                point.series.name +
                                ': ' +
                                point.y
                            );
                        }, '<b>' + useInsights().formatTimeSeriesChartDateByUnit(this.x, unit.value, selectedRange.value) + '</b>');
                    }
                },
                xAxis: {
                    labels: {
                        enabled: true,
                        formatter() {
                            return useInsights().formatTimeSeriesChartDateByUnit(
                                this.value,
                                unit.value,
                                selectedRange.value,
                                true
                            );
                        }
                    }
                }
            };

            const loadSeries = async () => {
                isLoading.value = true;

                const res = await useInsights().getTimeSeries(contact.id, dateRangeFrom.value, dateRangeTo.value);

                if (res && res.data?.total_conversions?.series[0] !== null) {
                    dataset.value = [
                        {
                            name: '# of Conversions',
                            data: res.data?.total_conversions?.series
                        }
                    ];

                    categories.value = res.data?.total_conversions?.range?.items;
                    unit.value = res.data?.total_conversions?.range?.unit;
                } else {
                    dataset.value = [];
                    categories.value = [];
                }

                isLoading.value = false;
            };

            const updateSeries = (range) => {
                if (selectedRange.value === range) {
                    return;
                }

                selectedRange.value = range;

                switch (range) {
                    case TOTAL_CONVERSIONS_RANGE_WEEK:
                        dateRangeTo.value = moment().format();
                        dateRangeFrom.value = moment().subtract(1, 'weeks').endOf('day').format();
                        break;

                    case TOTAL_CONVERSIONS_RANGE_MONTH:
                        dateRangeTo.value = moment().format();
                        dateRangeFrom.value = moment().subtract(1, 'months').endOf('day').format();
                        break;

                    default:
                    case TOTAL_CONVERSIONS_RANGE_ALL:
                        dateRangeTo.value = null;
                        dateRangeFrom.value = null;
                        break;
                }

                events.trackEvent(EVENT_NAME_CONVERSION_CHART_ADJUSTED, {
                    dl_contact_id: contact.id
                });

                loadSeries();
            };

            onBeforeMount(() => {
                loadSeries();
            });

            return {
                isLoading,
                dataset,
                categories,
                chartOverrides,
                updateSeries,
                selectedRange,
                TOTAL_CONVERSIONS_RANGES,
                TOTAL_CONVERSIONS_RANGE_LABELS
            };
        }
    };
</script>

<style lang="scss" scoped>
    .conversions-chart {
        border-radius: $g-border-radius;

        .chart-container {
            position: relative;
            min-height: 250px;
        }

        button {
            font-weight: $g-font-weight-semi-bold;

            &.selected {
                color: #5acbf1 !important;
                border-color: #5acbf1 !important;
            }
        }
    }
</style>
